import type { ReactNode } from 'react';

import FormField from '@/components/ui/form-field';
import { Textarea } from '@/components/ui/textarea';
import type { TextareaProps } from '@/components/ui/textarea';
import type { FormSpacing } from '@/theme/size-tokens';

export type FormTextareaProps = Omit<TextareaProps, 'validationState'> & {
  /** Label text displayed above the textarea */
  label?: string;
  /** Helper text displayed below textarea */
  helperText?: string;
  /** Spacing between label, textarea, and helper text */
  spacing?: FormSpacing;
  /** Error message to display */
  error?: string;
  /** Optional content rendered to the right of the label (e.g. an info tooltip) */
  labelSuffix?: ReactNode;
};

/**
 * FormTextarea - Textarea wrapped with FormField for label and error display
 *
 * @example
 * ```tsx
 * <FormTextarea
 *   label="Description"
 *   placeholder="Enter a description..."
 *   required
 *   minLength={10}
 * />
 * ```
 */
function FormTextarea({
  // FormField props
  label,
  helperText,
  spacing,
  error,
  required,
  disabled,
  id,
  onChange,
  labelSuffix,
  ...textareaProps
}: Readonly<FormTextareaProps>) {
  return (
    <FormField
      label={label}
      helperText={helperText}
      spacing={spacing}
      error={error}
      required={required}
      disabled={disabled}
      id={id}
      labelSuffix={labelSuffix}
    >
      <Textarea
        {...textareaProps}
        onChange={onChange}
        required={required}
        disabled={disabled}
        id={id}
      />
    </FormField>
  );
}

FormTextarea.displayName = 'FormTextarea';

export default FormTextarea;
